import { useCallback, useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import {
  WORKSPACE_SETTINGS_SECTIONS,
  scrollToWorkspaceSection,
  type WorkspaceSettingsSectionId,
} from "./workspace-settings-sections";

export function useWorkspaceSectionNav(isOwner: boolean) {
  const isWorkspacePage = useRouterState({
    select: (s) => s.location.pathname.endsWith("/settings/workspace"),
  });
  const hash = useRouterState({ select: (s) => s.location.hash });

  const [activeSectionId, setActiveSectionId] =
    useState<WorkspaceSettingsSectionId>("general");

  const scrollToSection = useCallback(
    (sectionId: WorkspaceSettingsSectionId) => {
      scrollToWorkspaceSection(sectionId);
      setActiveSectionId(sectionId);
    },
    [],
  );

  useEffect(() => {
    if (!isWorkspacePage) return;

    const hashSectionId = hash.replace("#", "") as WorkspaceSettingsSectionId;
    const isValidHash = WORKSPACE_SETTINGS_SECTIONS.some(
      (section) =>
        section.id === hashSectionId &&
        (!section.ownerOnly || isOwner),
    );

    if (isValidHash) {
      setActiveSectionId(hashSectionId);
      requestAnimationFrame(() => {
        scrollToWorkspaceSection(hashSectionId);
      });
    }
  }, [hash, isOwner, isWorkspacePage]);

  useEffect(() => {
    if (!isWorkspacePage) return;

    const visibleSections = WORKSPACE_SETTINGS_SECTIONS.filter(
      (section) => !section.ownerOnly || isOwner,
    );

    const sectionElements = visibleSections
      .map((section) => document.getElementById(section.id))
      .filter((element): element is HTMLElement => element !== null);

    if (sectionElements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        const topmost = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) =>
              a.target.getBoundingClientRect().top -
              b.target.getBoundingClientRect().top,
          )[0];

        const nextId = (topmost ?? visible[0])?.target.id;
        if (nextId) {
          setActiveSectionId(nextId as WorkspaceSettingsSectionId);
        }
      },
      { rootMargin: "-10% 0px -55% 0px", threshold: [0, 0.1, 0.5, 1] },
    );

    for (const element of sectionElements) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [isOwner, isWorkspacePage]);

  return { activeSectionId, scrollToSection, isWorkspacePage };
}
